import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from './firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';

type Category = 'love' | 'habits' | 'ambitions' | 'spirituality' | 'emotions';
type BeaconType = 'I' | 'You' | 'We';

interface BeaconEntry {
  id: string;
  text: string;
  type: BeaconType;
}

interface BeaconObject {
  text: string;
  type: BeaconType;
}

interface BeaconsData {
  love: BeaconObject[];
  habits: BeaconObject[];
  ambitions: BeaconObject[];
  spirituality: BeaconObject[];
  emotions: BeaconObject[];
}

const categories: { key: Category; label: string }[] = [
  { key: 'love', label: 'Love & Sensuality' },
  { key: 'habits', label: 'Habits & Personality' },
  { key: 'ambitions', label: 'Ambitions & Preferences' },
  { key: 'spirituality', label: 'Spirituality & Values' },
  { key: 'emotions', label: 'Emotions & Communication' }
];

const getTypeColor = (type: BeaconType): string => {
  if (type === 'I') return '#2196F3';
  if (type === 'You') return '#f44336';
  if (type === 'We') return '#9C27B0';
  throw new Error('Unknown beacon type');
};

function Beacons(): React.ReactElement {
  const user = auth.currentUser;
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [entries, setEntries] = useState<Record<Category, BeaconEntry[]>>({
    love: [{ id: '1', text: '', type: 'I' }],
    habits: [{ id: '2', text: '', type: 'I' }],
    ambitions: [{ id: '3', text: '', type: 'I' }],
    spirituality: [{ id: '4', text: '', type: 'I' }],
    emotions: [{ id: '5', text: '', type: 'I' }]
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState<boolean>(false);

  const updateEntry = (category: Category, id: string, text: string) => {
    setEntries(prev => {
      const updatedEntries = prev[category].map(entry =>
        entry.id === id ? { ...entry, text } : entry
      );

      // If the last entry now has text, add a new empty entry
      const lastEntry = updatedEntries[updatedEntries.length - 1];
      if (lastEntry.id === id && text.length > 0) {
        updatedEntries.push({
          id: Date.now().toString(),
          text: '',
          type: 'I'
        });
      }

      return {
        ...prev,
        [category]: updatedEntries
      };
    });
  };

  const updateEntryType = (category: Category, id: string, type: BeaconType) => {
    setEntries(prev => {
      const updatedEntries = prev[category].map(entry =>
        entry.id === id ? { ...entry, type } : entry
      );

      return {
        ...prev,
        [category]: updatedEntries
      };
    });
  };

  const deleteEntry = (category: Category, id: string) => {
    setEntries(prev => {
      const filtered = prev[category].filter(entry => entry.id !== id);

      // Always keep at least one entry
      if (filtered.length === 0) {
        filtered.push({
          id: Date.now().toString(),
          text: '',
          type: 'I'
        });
      }

      return {
        ...prev,
        [category]: filtered
      };
    });
  };

  const moveEntry = (category: Category, index: number, direction: 'up' | 'down') => {
    const newEntries = [...entries[category]];
    const newIndex = direction === 'up' ? index - 1 : index + 1;

    if (newIndex < 0 || newIndex >= newEntries.length) return;

    [newEntries[index], newEntries[newIndex]] = [newEntries[newIndex], newEntries[index]];

    setEntries(prev => ({
      ...prev,
      [category]: newEntries
    }));
  };

  // Load beacons from Firestore
  useEffect(() => {
    const loadBeacons = async () => {
      if (!user) return;

      try {
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          const data = userDoc.data();

          // Migration: check for old "beacons" field and migrate to "beacon"
          let beaconData = data.beacon as BeaconsData | undefined;
          if (!beaconData && data.beacons) {
            console.log('Migrating from old "beacons" field to "beacon"');
            beaconData = data.beacons as BeaconsData;
            // Migrate the data
            await updateDoc(userDocRef, {
              beacon: beaconData,
              beacons: null
            });
          }

          if (beaconData) {
            const convertCategory = (categoryData: BeaconObject[] | undefined, categoryKey: string): BeaconEntry[] => {
              if (!categoryData || categoryData.length === 0) {
                return [{ id: `${categoryKey}-0`, text: '', type: 'I' }];
              }

              return categoryData.map((obj, i) => ({
                id: `${categoryKey}-${i}`,
                text: obj.text,
                type: obj.type
              }));
            };

            const convertedEntries: Record<Category, BeaconEntry[]> = {
              love: convertCategory(beaconData.love, 'love'),
              habits: convertCategory(beaconData.habits, 'habits'),
              ambitions: convertCategory(beaconData.ambitions, 'ambitions'),
              spirituality: convertCategory(beaconData.spirituality, 'spirituality'),
              emotions: convertCategory(beaconData.emotions, 'emotions')
            };

            // Ensure each category has at least one empty entry at the end
            Object.keys(convertedEntries).forEach(key => {
              const category = key as Category;
              const lastEntry = convertedEntries[category][convertedEntries[category].length - 1];
              if (lastEntry?.text !== '') {
                convertedEntries[category].push({
                  id: `${category}-${Date.now()}`,
                  text: '',
                  type: 'I'
                });
              }
            });

            setEntries(convertedEntries);
          }
        }
      } catch (error) {
        console.error('Error loading beacons:', error);
      }

      setLoading(false);
      setInitialLoadComplete(true);
    };

    loadBeacons();
  }, [user]);

  // Auto-save beacons to Firestore
  useEffect(() => {
    if (!initialLoadComplete || !user) return;

    const timeoutId = setTimeout(async () => {
      try {
        // Convert from UI format to DB format (filter out empty entries)
        const beaconsData: BeaconsData = {
          love: entries.love.filter(e => e.text.trim().length > 0).map(e => ({ text: e.text, type: e.type })),
          habits: entries.habits.filter(e => e.text.trim().length > 0).map(e => ({ text: e.text, type: e.type })),
          ambitions: entries.ambitions.filter(e => e.text.trim().length > 0).map(e => ({ text: e.text, type: e.type })),
          spirituality: entries.spirituality.filter(e => e.text.trim().length > 0).map(e => ({ text: e.text, type: e.type })),
          emotions: entries.emotions.filter(e => e.text.trim().length > 0).map(e => ({ text: e.text, type: e.type }))
        };

        const userDocRef = doc(db, 'users', user.uid);
        await updateDoc(userDocRef, {
          beacon: beaconsData
        });
      } catch (error) {
        console.error('Error saving beacons:', error);
      }
    }, 5000);

    return () => clearTimeout(timeoutId);
  }, [entries, user, initialLoadComplete]);

  if (loading) {
    return <div style={{ padding: '20px' }}>Loading...</div>;
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <button
        onClick={() => navigate('/')}
        style={{
          padding: '10px 20px',
          fontSize: '16px',
          backgroundColor: '#2196F3',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          marginBottom: '20px'
        }}
      >
        Back to Profile
      </button>

      <h1>My Beacon</h1>

      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', justifyContent: 'center' }}>
        {categories.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setSelectedCategory(selectedCategory === key ? null : key)}
            style={{
              padding: '8px 16px',
              fontSize: '13px',
              backgroundColor: selectedCategory === key ? '#2196F3' : '#e0e0e0',
              color: selectedCategory === key ? 'white' : '#333',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: selectedCategory === key ? 'bold' : 'normal',
              whiteSpace: 'nowrap'
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {selectedCategory && (
        <div style={{ marginTop: '20px' }}>
          <h2>{categories.find(c => c.key === selectedCategory)?.label}</h2>

          {entries[selectedCategory].map((entry, index) => (
            <div
              key={entry.id}
              style={{
                display: 'flex',
                gap: '10px',
                marginBottom: '10px',
                alignItems: 'center'
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', flex: 1 }}>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  {(['I', 'You', 'We'] as BeaconType[]).map(type => (
                    <label
                      key={type}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        cursor: 'pointer',
                        padding: '6px 12px',
                        borderRadius: '20px',
                        backgroundColor: entry.type === type ? getTypeColor(type) : '#f5f5f5',
                        color: entry.type === type ? 'white' : '#333',
                        fontWeight: entry.type === type ? 'bold' : 'normal',
                        fontSize: '13px',
                        border: entry.type === type ? 'none' : '2px solid #e0e0e0'
                      }}
                    >
                      <input
                        type="radio"
                        name={`beacon-type-${entry.id}`}
                        checked={entry.type === type}
                        onChange={() => updateEntryType(selectedCategory, entry.id, type)}
                        style={{ margin: 0 }}
                      />
                      {type}
                    </label>
                  ))}
                </div>

                <textarea
                  value={entry.text}
                  onChange={(e) => updateEntry(selectedCategory, entry.id, e.target.value)}
                  placeholder="Write your beacon..."
                  style={{
                    padding: '10px',
                    fontSize: '14px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    resize: 'vertical',
                    minHeight: '60px',
                    backgroundColor: entry.type === 'I' ? '#E3F2FD' : entry.type === 'You' ? '#FFEBEE' : '#F3E5F5'
                  }}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <button
                  onClick={() => moveEntry(selectedCategory, index, 'up')}
                  disabled={index === 0}
                  style={{
                    padding: '5px 10px',
                    fontSize: '12px',
                    backgroundColor: index === 0 ? '#e0e0e0' : '#2196F3',
                    color: index === 0 ? '#999' : 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: index === 0 ? 'not-allowed' : 'pointer'
                  }}
                >
                  ▲
                </button>
                <button
                  onClick={() => moveEntry(selectedCategory, index, 'down')}
                  disabled={index === entries[selectedCategory].length - 1}
                  style={{
                    padding: '5px 10px',
                    fontSize: '12px',
                    backgroundColor: index === entries[selectedCategory].length - 1 ? '#e0e0e0' : '#2196F3',
                    color: index === entries[selectedCategory].length - 1 ? '#999' : 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: index === entries[selectedCategory].length - 1 ? 'not-allowed' : 'pointer'
                  }}
                >
                  ▼
                </button>
              </div>

              {entry.text.length > 0 && (
                <button
                  onClick={() => deleteEntry(selectedCategory, entry.id)}
                  style={{
                    padding: '5px 10px',
                    fontSize: '12px',
                    backgroundColor: '#f44336',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Delete
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Beacons;
